import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOnboardingHub } from '@/hooks/useOnboardingHub';
import { requestTourStart } from '@/components/guided-tour/tourBus';

/**
 * Dashboard top-right launcher (company_admin only).
 * "Getting Started" → next page whose tour is still active, starts it there.
 * "Take a tour" once nothing is left → menu of all tours for replay.
 */
export function GettingStartedButton({ user, className }) {
  const navigate = useNavigate();
  const { loadProgress } = useOnboardingHub();
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await loadProgress();
      setState(data);
    } catch {
      setState(null);
    }
  }, [loadProgress]);

  useEffect(() => {
    if (user?.role !== 'company_admin') return;
    refresh();
  }, [user?.role, refresh]);

  if (user?.role !== 'company_admin' || !state?.part_a_completed) return null;

  const pages = (state.pages || []).filter((p) => p.available !== false);
  const next = state.next_undone;

  const launch = (page) => {
    if (!page) return;
    navigate(page.path);
    // Let the page mount before the tour looks for its anchors.
    setTimeout(() => requestTourStart(page.slug), 400);
  };

  if (next) {
    return (
      <Button
        onClick={() => launch(next)}
        className={`bg-violet-600 text-white hover:bg-violet-700 ${className || ''}`}
        data-testid="getting-started-btn"
      >
        <Sparkles className="mr-2 h-4 w-4" />
        Getting Started
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={className} data-testid="take-a-tour-btn">
          <Compass className="mr-2 h-4 w-4" />
          Take a tour
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Replay a walkthrough</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {pages.map((p) => (
          <DropdownMenuItem key={p.key} onClick={() => launch(p)}>{p.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default GettingStartedButton;
